<template>
  <div class="card-profile" :class="{ page }">
    <spinner v-if="!loaded" />

    <template v-else>
      <card-profile-actions
        :card-id="cardId"
        :card="card"
        :page="page"
        v-on="$listeners"
      />

      <card-profile-details
        :card="card"
        :card-id="cardId"
        :is-admin="isAdmin"
        :is-staff="isStaff"
        :page="page"
        @edit="$emit('edit')"
      />

      <card-profile-collection-investment :card="card" />

      <card-profile-watchlist-details v-if="hasWatchlistData" :card="card" />

      <card-profile-collection-notes :card="card" />

      <card-profile-admin
        v-if="isStaff && cardId"
        :card="card"
        :card-id="cardId"
        :page="page"
      />

      <card-profile-sales-graph
        v-if="showCardProfileSalesGraph"
        :card="card"
        :card-id="cardId"
        :zoom.sync="zoom"
        :scale.sync="scale"
        :page="page"
        @saleClicked="handleGraphSaleClicked"
      />

      <section v-if="profileForList" class="card-profile-pop-section">
        <h4 class="secondary-font">
          Profile
        </h4>

        <div class="card-profile-pop-item" @click="goToProfile">
          <profile-list-item :profile="profileForList" />
        </div>
      </section>

      <card-profile-index :card="card" />

      <card-profile-shop :card="card" :listings="listings" />

      <similar-cards
        v-if="cardId"
        :id="card.id"
        :similar-cards="card.similarCards"
        :page="page"
      />

      <card-profile-information :card="card" />

      <card-profile-set-information :card="card" />

      <card-profile-multipliers :card="card" />

      <card-profile-references :card="card" />
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import * as Sentry from '@sentry/vue';

import firebase from 'firebase/compat/app';
import { authService, cardService, searchService } from '@/services';
import { getDailySalesFromSalesSearch } from '@cardladder/shared/utils/sales.util';

import CardProfileDetails from '@cardladder/shared/components/CardProfileDetails';
import CardProfileInformation from '@cardladder/shared/components/CardProfileInformation';
import CardProfileReferences from '@cardladder/shared/components/CardProfileReferences';
import CardProfileSalesGraph from '@cardladder/shared/components/CardProfileSalesGraph';
import CardProfileSetInformation from '@cardladder/shared/components/CardProfileSetInformation';
import CardProfileShop from '@cardladder/shared/components/CardProfileShop';
import SimilarCards from '@cardladder/shared/components/SimilarCards';

import CardProfileActions from './CardProfileActions';
import CardProfileAdmin from './CardProfileAdmin';
import CardProfileCollectionInvestment from './CardProfileCollectionInvestment';
import CardProfileCollectionNotes from './CardProfileCollectionNotes';
import CardProfileMultipliers from './CardProfileMultipliers';
import CardProfileIndex from './CardProfileIndex';
import CardProfileWatchlistDetails from './CardProfileWatchlistDetails';
import ProfileListItem from '@/profiles/ProfileListItem';

export default {
  components: {
    CardProfileDetails,
    CardProfileInformation,
    CardProfileReferences,
    CardProfileSalesGraph,
    CardProfileSetInformation,
    CardProfileShop,
    CardProfileActions,
    CardProfileAdmin,
    CardProfileCollectionInvestment,
    CardProfileCollectionNotes,
    CardProfileMultipliers,
    CardProfileIndex,
    CardProfileWatchlistDetails,
    SimilarCards,
    ProfileListItem,
  },

  props: {
    id: String,
    page: Boolean,
    profileType: String,
  },

  data() {
    return {
      card: {},
      cardId: null,
      listings: [],
      loaded: false,
      profileForList: null,
    };
  },

  created() {
    this.load();
  },

  mounted() {
    this.$bus.$on('card-profile-changed', this.load);

    if (this.$route.query.edit) {
      setTimeout(() => {
        this.$bus.$emit('admin-edit-card', this.cardId);
      });
    }
  },

  beforeDestroy() {
    this.$bus.$off('card-profile-changed', this.load);
  },

  watch: {
    id(to, from) {
      if (to !== from) {
        this.load();
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
      collections: (state) => state.collections,
    }),

    ...mapGetters({
      isAdmin: 'isAdmin',
      isStaff: 'isStaff',
    }),

    zoom: {
      get() {
        return this.$store.state.cardGraphZoom;
      },

      set(value) {
        this.$store.commit('SET_CARD_GRAPH_ZOOM', value);
      },
    },

    scale: {
      get() {
        return this.$store.state.cardGraphScale;
      },

      set(value) {
        this.$store.commit('SET_CARD_GRAPH_SCALE', value);
      },
    },

    hasCollectionData() {
      return Boolean(this.card && this.card.collectionCardId);
    },

    hasWatchlistData() {
      return Boolean(this.card && this.card.watchlistCardId);
    },

    showCardProfileSalesGraph() {
      if (this.cardId) return true;

      if (!this.card || !this.card.dailySales || Object.keys(this.card.dailySales).length === 0) return false;

      return this.card.useGradedCardAutomatic || this.card.playerIndexId || this.card.otherIndexId || this.card.playerIndexPrice || this.card.playerIndexDate || this.card.multipliers?.length > 0;
    },
  },

  methods: {
    loadProfileForList() {
      this.profileForList = null;
      if (!this.card.profileId) {
        return;
      }
      const profileId = this.card.profileId;
      firebase.firestore().collection('profiles').doc(profileId).get()
        .then((snap) => {
          if (!snap.exists) {
            this.profileForList = null;
            return;
          }
          this.profileForList = { ...snap.data(), id: snap.id };
        })
        .catch((e) => {
          Sentry.captureException(e);
          this.profileForList = null;
        });
    },

    goToProfile() {
      if (!this.card.profileId) return;
      this.$router.push(`/profiles/${this.card.profileId}`);
    },

    async load() {
      try {
        this.loaded = false;
        this.card = {};
        this.profileForList = null;

        const promises = [];
        const collectionIds = this.collections.map(({ id }) => id);

        if (this.profileType === 'collection') {
          const collectionCard = await authService.getCollectionCard(this.user.uid, this.collection.id, this.id);

          if (collectionCard) {
            this.card = collectionCard;
            this.card.collectionCardId = collectionCard.id;

            if (collectionCard.cardId) {
              this.cardId = collectionCard.cardId;
              promises.push(cardService.getCard(collectionCard.cardId));
              promises.push(authService.getWatchlistCardByCardId(this.user.uid, collectionCard.cardId));
            } else {
              this.cardId = null;

              const shouldPullSalesFromProfileInfo = this.card.useGradedCardAutomatic && this.card.profileId && this.card.grader && this.card.grade;

              if (this.card.ebayQuery || shouldPullSalesFromProfileInfo) {
                try {
                  const input = {
                    index: 'salesarchive',
                    page: 0,
                    limit: 100,
                    sort: 'date',
                  };

                  if (shouldPullSalesFromProfileInfo) {
                    input.filters = {
                      profileId: this.card.profileId,
                      grade: this.card.grade,
                      grader: this.card.grader,
                      qualifierType: this.card.qualifierType,
                      autographGrade: this.card.autographGrade,
                    };
                  } else if (this.card.ebayQuery) {
                    input.query = this.card.ebayQuery;
                  }

                  const data = await searchService.search(input);
                  const dailySalesResponse = getDailySalesFromSalesSearch(data.hits);

                  this.card.dailySales = dailySalesResponse.dailySales;
                  this.card.numSales = dailySalesResponse.numSales;

                  if (!this.card.stats) {
                    this.card.stats = {};
                  }

                  if (dailySalesResponse.newestSale) {
                    this.card.stats.newestSale = dailySalesResponse.newestSale;
                  }
                } catch (e) {
                  Sentry.captureException(e);
                }
              }
            }
          } else {
            this.backToCollection();
          }
        } else if (this.profileType === 'watchlist') {
          const watchlistCard = await authService.getWatchlistCard(this.user.uid, this.id);

          if (watchlistCard) {
            this.card = watchlistCard;
            this.card.watchlistCardId = watchlistCard.id;

            if (watchlistCard.cardId) {
              this.cardId = watchlistCard.cardId;
              promises.push(cardService.getCard(watchlistCard.cardId));
              promises.push(authService.getCollectionCardByCardId(this.user.uid, collectionIds, watchlistCard.cardId));
            } else {
              this.cardId = null;
            }
          } else {
            this.backToWatchlist();
          }
        } else {
          this.cardId = this.id;
          promises.push(cardService.getCard(this.id));
          promises.push(authService.getCollectionCardByCardId(this.user.uid, collectionIds, this.id));
          promises.push(authService.getWatchlistCardByCardId(this.user.uid, this.id));
        }

        const promiseResults = await Promise.all(promises);

        promiseResults.forEach((promiseResult) => {
          if (promiseResult === null) {
            this.backToLadder();
          } else {
            this.card = {
              ...this.card,
              ...promiseResult,
              collectionCardId: this.card.collectionCardId || promiseResult.collectionCardId || null,
              watchlistCardId: this.card.watchlistCardId || promiseResult.watchlistCardId || null,
              pop: this.card.pop || promiseResult.pop || null,
              ebayQuery: this.card.ebayQuery || promiseResult.ebayQuery || '',
              profileId: this.card.profileId || promiseResult.profileId || null,
              cert: this.card.cert || null,
            };
          }
        });

        this.card = {
          ...this.card,
          alertAbovePrice: this.card.alertAbovePrice || null,
          alertBelowPrice: this.card.alertBelowPrice || null,
          priceMovement: this.card.priceMovement || 0,
        };

        this.getListings();
        this.loadProfileForList();
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loaded = true;
    },

    backToLadder() {
      this.$router.replace('/ladder');
    },

    backToWatchlist() {
      this.$router.replace('/watchlist');
    },

    backToCollection() {
      this.$router.replace('/collection');
    },

    async getListings() {
      if (!this.card.ebayQuery) {
        return;
      }

      const data = await searchService.search({
        index: 'listings',
        sort: 'watchers',
        query: this.card.ebayQuery,
        page: 0,
        limit: 5,
        filters: {
          endDate: 'now',
        },
      });

      this.listings = data.hits;
    },

    handleGraphSaleClicked(sale) {
      if (sale && sale.label && sale.label instanceof Date && this.profileType !== 'collection' && this.profileType !== 'watchlist') {
        const query = { ...this.$route.query };

        query.date = sale.label.toISOString();
        query.showSales = true;

        this.$router.replace({ query });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-profile {
  min-height: 100%;

  &:not(.page) {
    padding: $gp;
  }
}

.card-profile-pop-section {
  padding-top: $gp;
  border-top: solid 1px var(--theme-border);
  margin-bottom: $gp * 1.5;
}

.card-profile-pop-item {
  cursor: pointer;
  margin: 0 (-$gp);

  &:hover {
    background-color: var(--theme-highlight);
  }
}
</style>
